import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button } from '../components/Button';
import { ScreenHeader } from '../components/ScreenHeader';
import { useProfile } from '../hooks/useProfile';
import { useGabarit } from '../hooks/useGabarit';
import { getAuthEmail } from '../services/profile';
import { isValidEmail, isValidUrl } from '../utils/validators';
import { colonneFormulaire, colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  linkedin_url: string;
}

const EMPTY: FormState = { first_name: '', last_name: '', email: '', phone: '', address: '', linkedin_url: '' };

interface Field {
  key: keyof FormState;
  label: string;
  placeholder: string;
  keyboardType?: 'email-address' | 'phone-pad' | 'url';
}

// Construit AU RENDU : constante de module = libellés figés à l'import. Les CLÉS sont celles des
// colonnes de `profiles`, elles ne se traduisent jamais.
const fields = (): Field[] => [
  { key: 'first_name', label: t('personalInfo.firstName'), placeholder: t('personalInfo.firstNamePlaceholder') },
  { key: 'last_name', label: t('personalInfo.lastName'), placeholder: t('personalInfo.lastNamePlaceholder') },
  {
    key: 'email',
    label: t('personalInfo.email'),
    placeholder: t('personalInfo.emailPlaceholder'),
    keyboardType: 'email-address',
  },
  {
    key: 'phone',
    label: t('personalInfo.phone'),
    placeholder: t('personalInfo.phonePlaceholder'),
    keyboardType: 'phone-pad',
  },
  { key: 'address', label: t('personalInfo.address'), placeholder: t('personalInfo.addressPlaceholder') },
  {
    key: 'linkedin_url',
    label: t('personalInfo.linkedin'),
    placeholder: t('personalInfo.linkedinPlaceholder'),
    keyboardType: 'url',
  },
];

export function PersonalInfoScreen({ onBack }: { onBack?: () => void }) {
  const { profile, loading, error, save, saveState, saveError } = useProfile();
  const { large } = useGabarit();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [validation, setValidation] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Préremplissage dès que le profil est chargé (email : profil → email de connexion).
  useEffect(() => {
    if (!profile) return;
    setForm({
      first_name: profile.first_name ?? '',
      last_name: profile.last_name ?? '',
      email: profile.email ?? getAuthEmail() ?? '',
      phone: profile.phone ?? '',
      address: profile.address ?? profile.city ?? '',
      linkedin_url: profile.linkedin_url ?? '',
    });
  }, [profile]);

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidation(null);
    setDirty(true);
  };

  const submit = async () => {
    setValidation(null);
    if (form.email.trim() && !isValidEmail(form.email)) {
      setValidation('Adresse email invalide.');
      return;
    }
    if (!isValidUrl(form.linkedin_url)) {
      setValidation('URL LinkedIn invalide (commence par https://).');
      return;
    }
    const ok = await save({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      linkedin_url: form.linkedin_url.trim() || null,
    });
    if (ok) setDirty(false);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} testID="pi-back" />
        <View style={styles.centered}>
          <ActivityIndicator testID="pi-loading" color={colors.bluePrimary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} testID="pi-back" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={[styles.content, large ? styles.contentL : colonneFormulaire]} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
          <Text style={styles.title}>{t('misc.personalInfoTitle')}</Text>

          {error ? (
            <Text testID="pi-load-error" style={styles.error}>
              {error}
            </Text>
          ) : null}

          <View style={large ? styles.grilleL : styles.grille}>
          {fields().map((field) => (
            <View key={field.key} style={[styles.field, large && styles.fieldL]}>
              <Text style={styles.label}>{field.label}</Text>
              <TextInput
                testID={`pi-${field.key}`}
                style={styles.input}
                placeholder={field.placeholder}
                placeholderTextColor={colors.grey400}
                autoCapitalize={field.keyboardType === 'email-address' || field.keyboardType === 'url' ? 'none' : 'sentences'}
                keyboardType={field.keyboardType ?? 'default'}
                value={form[field.key]}
                onChangeText={(v) => setField(field.key, v)}
              />
            </View>
          ))}
          </View>

          {validation ? (
            <Text testID="pi-validation" style={styles.error}>
              {validation}
            </Text>
          ) : null}

          {saveState === 'error' ? (
            <Text testID="pi-error" style={styles.error}>
              {saveError ?? 'Enregistrement impossible.'} Réessaie.
            </Text>
          ) : null}
          {saveState === 'saved' && !dirty ? (
            <Text testID="pi-saved" style={styles.saved}>
              Enregistré ✓
            </Text>
          ) : null}

          <View style={large ? styles.boutonL : undefined}>
            <Button
              title={t('misc.save')}
              onPress={submit}
              loading={saveState === 'saving'}
              testID="pi-save"
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  content: { padding: screenPadding, gap: spacing.md },
  // Ordinateur : les six champs sur deux colonnes, le bouton à gauche sous les champs.
  contentL: { width: '100%', maxWidth: 920, alignSelf: 'center', padding: spacing.xxl + spacing.xl, gap: spacing.xl },
  grille: { gap: spacing.md },
  grilleL: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg },
  fieldL: { flexBasis: '45%', flexGrow: 1, minWidth: 280 },
  boutonL: { alignSelf: 'flex-start', minWidth: 260 },
  title: { fontSize: typography.h1.size, fontWeight: typography.h1.weight, color: colors.grey900 },
  field: { gap: spacing.xs },
  label: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  input: {
    minHeight: 48,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    fontSize: typography.body.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },
  error: { color: colors.error, fontSize: typography.small.size },
  saved: { color: colors.success, fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight },
});
